// src/sectors/pilates/attendance/ui/AttendanceMarkModal/attendanceUtils.js
import { S } from './attendanceConstants';

// =========================
// basic format helpers
// =========================
export function pad2(n) {
  return String(n).padStart(2, '0');
}

export function minToHm(min) {
  const m = Number(min) || 0;
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

export function shortDateTime(isoOrInstant) {
  if (!isoOrInstant) return null;
  const s = String(isoOrInstant);
  if (s.includes('T')) return s.replace('T', ' ').slice(0, 16);
  return s;
}

export function safeUpper(s) {
  return String(s || '').toUpperCase();
}

export function errText(e) {
  return e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Bir hata oluştu.';
}

// =========================
// attendance mapping
// =========================
export function requiresNote(status) {
  return status === S.CANCELLED || status === S.BURN;
}

export function uiStatusToBackend(status) {
  if (status === S.PENDING) return 'PENDING';
  if (status === S.PRESENT) return 'ATTENDED';
  if (status === S.ABSENT) return 'NO_SHOW';
  if (status === S.BURN) return 'BURNED';
  if (status === S.CANCELLED) return 'CANCELLED';
  return 'PENDING';
}

export function backendToUiStatus(backendStatus) {
  const as = safeUpper(backendStatus);
  if (as === 'PENDING') return S.PENDING;
  if (as === 'ATTENDED') return S.PRESENT;
  if (as === 'NO_SHOW') return S.ABSENT;
  if (as === 'BURNED') return S.BURN;
  if (as === 'CANCELLED') return S.CANCELLED;
  return S.PENDING;
}

export function isMarkedUiStatus(uiStatus) {
  return uiStatus !== S.PENDING;
}

// =========================
// UI lines (package + remaining + last5)
// =========================
export function remainingText(p) {
  const rn = p?.remainingNow;
  if (!rn) return null;

  const n = rn?.normalRemaining;
  const m = rn?.makeUpRemaining;

  if (n == null && m == null) return null;
  if (m == null) return `Kalan: ${n ?? 0}`;
  return `Kalan: ${n ?? 0} • Telafi: ${m ?? 0}`;
}

export function planText(p) {
  const u = p?.packageUsage;
  if (!u) return null;

  const name = u.planName || 'Paket';

  const total = u.totalSessions ?? null;
  const used = u.usedSessions ?? null;

  const tmu = u.totalMakeUp ?? null;
  const umu = u.usedMakeUp ?? null;

  // ✅ daha okunur: used/total
  const a = total != null && used != null ? `${used}/${total} seans` : null;
  const b = tmu != null && umu != null ? `${umu}/${tmu} telafi` : null;

  return [name, [a, b].filter(Boolean).join(' • ')].filter(Boolean).join(' — ');
}

export function last5Text(p) {
  const l = Array.isArray(p?.last5) ? p.last5 : [];
  if (!l.length) return 'Son seans yok';

  return l
    .slice(0, 5)
    .map((x) => {
      const d = x?.dayISO ? String(x.dayISO).slice(5) : '—';
      const st = safeUpper(x?.attendanceStatus);

      // ✅ ikon: attended ✓, no_show ×, burned 🔥, cancelled ⏸, pending •
      const tag =
        st === 'ATTENDED'
          ? '✓'
          : st === 'NO_SHOW'
          ? '×'
          : st === 'BURNED'
          ? '🔥'
          : st === 'CANCELLED'
          ? '⏸'
          : '•';

      return `${d} ${tag}`;
    })
    .join('  ');
}

export function hasWarningsOrActions(dec) {
  const hasWarn = (dec?.warnings?.length || 0) > 0;
  const hasAct =
    (dec?.actions?.length || 0) > 0 ||
    (dec?.participants || []).some((p) => (p?.actions?.length || 0) > 0) ||
    (dec?.participants || []).some((p) => (p?.warnings?.length || 0) > 0);

  return hasWarn || hasAct;
}

// =========================
// TR date helpers
// =========================
export function fmtTimeTR(isoOrDate) {
  if (!isoOrDate) return '—';
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return d.toLocaleString('tr-TR', { hour12: false });
}

export function fmtDateTR(iso) {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('tr-TR');
}

export function monthPackLabelFromISO(iso) {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  const m = d.toLocaleString('tr-TR', { month: 'long' });
  const y = d.getFullYear();
  const mm = `${m.charAt(0).toUpperCase()}${m.slice(1)}`;
  return `${mm} ${y} Paketi`;
}

export function monthNameTr(monthIndex1to12) {
  const m = Number(monthIndex1to12);
  const map = {
    1: 'Ocak',
    2: 'Şubat',
    3: 'Mart',
    4: 'Nisan',
    5: 'Mayıs',
    6: 'Haziran',
    7: 'Temmuz',
    8: 'Ağustos',
    9: 'Eylül',
    10: 'Ekim',
    11: 'Kasım',
    12: 'Aralık',
  };
  return map[m] || '—';
}

export function parseISODate(iso) {
  if (!iso) return null;
  const [y, mo, d] = String(iso)
    .split('-')
    .map((x) => Number(x));
  if (!y || !mo || !d) return null;
  return { y, mo, d };
}

// ✅ Paket hangi aya “ait” labelı + bitmiş tag’i
export function packageMonthLabelFrom(participant, sessionDayISO) {
  const startISO = participant?.packageStartAt || participant?.packageStartAtSnapshot;
  const endISO =
    participant?.packageEstimatedEndAt ||
    participant?.packageEstimatedEndAtSnapshot ||
    participant?.packageEndAt;

  const start = parseISODate(startISO);
  const sess = parseISODate(sessionDayISO);

  // label ayı: startAt varsa onu kullan, yoksa seans ayı
  const base = start || sess;
  if (!base) return null;

  let suffix = '';

  // bitmiş kontrolü: end < session
  const end = parseISODate(endISO);
  if (end && sess) {
    const endKey = end.y * 10000 + end.mo * 100 + end.d;
    const sessKey = sess.y * 10000 + sess.mo * 100 + sess.d;
    if (sessKey > endKey) suffix = ' • Bitmiş';
  }

  return `${monthNameTr(base.mo)} ${base.y} Paketi${suffix}`;
}

export function packagePeriodLabel(participant) {
  const startISO = participant?.packageStartAt || participant?.packageStartAtSnapshot;
  const endISO =
    participant?.packageEstimatedEndAt ||
    participant?.packageEstimatedEndAtSnapshot ||
    participant?.packageEndAt;

  if (!startISO && !endISO) return null;

  const start = startISO ? startISO : '—';
  const end = endISO ? endISO : '—';
  return `${start} → ${end}`;
}
